'use client';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTagadaContext } from '../providers/TagadaProvider';
import { TagadaError, TagadaErrorCode } from '../../core/errors';
/**
 * Hook to fetch and manage geolocation data - V2 Implementation
 * Compatible with V1 interface while using V2 provider architecture
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { data, isLoading, error, fetchGeoData } = useGeoLocation();
 *
 *   if (isLoading) return <div>Loading location...</div>;
 *   if (error) return <div>Error: {error}</div>;
 *
 *   return (
 *     <div>
 *       <p>Country: {data?.country}</p>
 *       <p>City: {data?.city}</p>
 *       <p>IP: {data?.ip_address}</p>
 *     </div>
 *   );
 * }
 * ```
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { fetchGeoData } = useGeoLocation({ autoFetch: false });
 *
 *   const handleClick = () => {
 *     fetchGeoData('8.8.8.8'); // Fetch for specific IP
 *   };
 *
 *   return <button onClick={handleClick}>Get Location for Google DNS</button>;
 * }
 * ```
 */
export function useGeoLocation(options = {}) {
    const { autoFetch = true, ip, refetchOnMount = false } = options;
    const { apiService } = useTagadaContext();
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    // Helper function to check if IP is localhost
    const isLocalhost = useCallback((ipAddress) => {
        if (!ipAddress)
            return false;
        return ipAddress === '127.0.0.1' || ipAddress === '::1' || ipAddress === 'localhost';
    }, []);
    // Helper function to validate IP address format
    const isValidIP = useCallback((ipAddress) => {
        if (!ipAddress)
            return false;
        // IPv4 regex pattern
        const ipv4Pattern = /^(\d{1,3}\.){3}\d{1,3}$/;
        // IPv6 regex pattern (simplified)
        const ipv6Pattern = /^([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$/;
        if (!ipv4Pattern.test(ipAddress) && !ipv6Pattern.test(ipAddress)) {
            return false;
        }
        // Additional validation for IPv4
        if (ipv4Pattern.test(ipAddress)) {
            const parts = ipAddress.split('.');
            return parts.every((part) => {
                const num = parseInt(part, 10);
                return num >= 0 && num <= 255;
            });
        }
        return true;
    }, []);
    // Function to fetch geolocation data
    const fetchGeoData = useCallback(async (customIp) => {
        if (!apiService) {
            setError('API service not available');
            return;
        }
        setIsLoading(true);
        setError(null);
        try {
            const targetIp = customIp || ip;
            const endpoint = targetIp ? `/api/v1/geo?ip=${encodeURIComponent(targetIp)}` : '/api/v1/geo';
            const geoData = await apiService.fetch(endpoint, {
                method: 'GET',
                skipAuth: true, // Geolocation endpoint doesn't require authentication
            });
            setData(geoData);
        }
        catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to fetch geolocation data';
            setError(errorMessage);
        }
        finally {
            setIsLoading(false);
        }
    }, [apiService, ip]);
    // Function to clear data and error state
    const clearData = useCallback(() => {
        setData(null);
        setError(null);
    }, []);
    // Auto-fetch on mount if enabled
    useEffect(() => {
        if (autoFetch && apiService) {
            void fetchGeoData();
        }
    }, [autoFetch, apiService, fetchGeoData]);
    // Refetch on mount if enabled
    useEffect(() => {
        if (refetchOnMount && apiService) {
            void fetchGeoData();
        }
    }, [refetchOnMount, apiService, fetchGeoData]);
    const tagadaError = useMemo(() => error ? new TagadaError(error, { code: TagadaErrorCode.NETWORK_ERROR }) : null, [error]);
    return {
        data,
        isLoading,
        error: tagadaError,
        fetchGeoData,
        clearData,
        isLocalhost: isLocalhost(data?.ip_address),
        isValidIP: isValidIP(data?.ip_address),
    };
}
